import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import
{
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    parse,
    isToday
} from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { fetchHFAvailableDates } from '../../lib/hf-utils';

interface HistoricalDatePickerProps
{
    symbol: string;
    currentDateStr: string | null; // e.g. "2026-04-12"
    onChange: ( dateStr: string ) => void;
    className?: string;
    hideIcon?: boolean;
}

export const HistoricalDatePicker: React.FC<HistoricalDatePickerProps> = ( { symbol, currentDateStr, onChange, className, hideIcon } ) =>
{
    const [ isOpen, setIsOpen ] = useState( false );
    const [ coords, setCoords ] = useState( { left: 0, top: 0 } );
    const triggerRef = useRef<HTMLDivElement>( null );

    // Parse currently selected date, default to today
    const parsedCurrentDate = currentDateStr ? parse( currentDateStr, 'yyyy-MM-dd', new Date() ) : new Date();

    // View month state (tracks which month the calendar grid is displaying)
    const [ viewDate, setViewDate ] = useState<Date>( parsedCurrentDate );

    // HF Available Dates Cache Set for the current view month
    const [ availableDays, setAvailableDays ] = useState<Set<number>>( new Set() );
    const [ isLoadingDays, setIsLoadingDays ] = useState( false );

    const popoverRef = useRef<HTMLDivElement>( null );

    // Close on click outside
    useEffect( () =>
    {
        const handleClickOutside = ( event: MouseEvent ) =>
        {
            if (
                popoverRef.current && !popoverRef.current.contains( event.target as Node ) &&
                triggerRef.current && !triggerRef.current.contains( event.target as Node )
            )
            {
                setIsOpen( false );
            }
        };
        if ( isOpen )
        {
            document.addEventListener( 'mousedown', handleClickOutside );
        }
        return () => document.removeEventListener( 'mousedown', handleClickOutside );
    }, [ isOpen ] );

    // Fetch Available Dates when the view month or symbol changes
    useEffect( () =>
    {
        let isMounted = true;
        const loadAvailability = async () =>
        {
            if ( !isOpen ) return; // Wait to load until popover is open
            setIsLoadingDays( true );
            const year = viewDate.getFullYear();
            const month = viewDate.getMonth() + 1; // 1-indexed

            const daysSet = await fetchHFAvailableDates( symbol, year, month );

            if ( isMounted )
            {
                setAvailableDays( daysSet );
                setIsLoadingDays( false );
            }
        };

        loadAvailability();
        return () => { isMounted = false; }
    }, [ viewDate, symbol, isOpen ] );

    // Handle Month Navigation
    const nextMonth = () => setViewDate( addMonths( viewDate, 1 ) );
    const prevMonth = () => setViewDate( subMonths( viewDate, 1 ) );

    // Handle date selection
    const handleSelectDate = ( date: Date ) =>
    {
        const formattedDate = format( date, 'yyyy-MM-dd' );
        onChange( formattedDate );
        setIsOpen( false );
    };

    // Calculate grid
    const monthStart = startOfMonth( viewDate );
    const monthEnd = endOfMonth( monthStart );
    const startDate = startOfWeek( monthStart, { weekStartsOn: 1 } ); // Monday start
    const endDate = endOfWeek( monthEnd, { weekStartsOn: 1 } );

    const daysInterval = eachDayOfInterval( { start: startDate, end: endDate } );
    const weekDayNames = [ 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su' ];

    const handleToggle = () => {
        if ( !isOpen ) {
            if ( triggerRef.current ) {
                const rect = triggerRef.current.getBoundingClientRect();
                const left = Math.min( rect.left, window.innerWidth - 290 );
                setCoords( { left, top: rect.bottom + 8 } );
            }
            setIsOpen( true );
        } else {
            setIsOpen( false );
        }
    };

    return (
        <div className="relative">
            {/* Trigger Button */ }
            <button
                type="button"
                ref={ triggerRef as any }
                onClick={ handleToggle }
                className={className || "flex items-center outline-none bg-transparent border-none p-0"}
            >
                <span className={`text-[12px] md:text-[13px] font-black tracking-tight premium-cell-mono transition-colors uppercase whitespace-nowrap ${className ? 'flex-1 text-left' : 'text-slate-700 dark:text-slate-100 hover:text-amber-600 dark:hover:text-amber-400'}`}>
                    { currentDateStr ? format( parsedCurrentDate, 'dd-MM-yyyy' ) : 'Select Date' }
                </span>
                {className && !hideIcon && <CalendarDays className="w-5 h-5 text-slate-400" />}
            </button>

            {/* Popover Calendar */ }
            { isOpen && typeof document !== 'undefined' && createPortal(
                <div 
                    ref={ popoverRef }
                    className="fixed z-[9999] w-[280px] p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl animate-in slide-in-from-top-2 fade-in duration-200"
                    style={{ left: coords.left, top: coords.top }}
                >

                    {/* Header */ }
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={ ( e ) => { e.stopPropagation(); prevMonth(); } }
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-500 transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <h2 className="text-sm font-bold text-slate-700 dark:text-slate-200 premium-cell-mono">
                            { format( viewDate, 'MMMM yyyy' ) }
                        </h2>
                        <button
                            onClick={ ( e ) => { e.stopPropagation(); nextMonth(); } }
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-500 transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Weekdays Row */ }
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        { weekDayNames.map( day => (
                            <div key={ day } className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                { day }
                            </div>
                        ) ) }
                    </div>

                    {/* Days Grid */ }
                    <div className="grid grid-cols-7 gap-1">
                        { daysInterval.map( ( day, idx ) =>
                        {
                            const isCurrentMonth = isSameMonth( day, viewDate );
                            const dayNumber = day.getDate();
                            const isSelected = isSameDay( day, parsedCurrentDate );

                            // Check against Hugging Face payload Set
                            const isAvailable = isCurrentMonth && availableDays.has( dayNumber );

                            // Dynamic Styling
                            let dayClasses = "relative h-8 w-8 flex items-center justify-center rounded-full text-xs font-bold transition-all duration-200 ";

                            if ( !isCurrentMonth )
                            {
                                dayClasses += "text-slate-300 dark:text-slate-600 cursor-not-allowed";
                            } else if ( isSelected )
                            {
                                dayClasses += "bg-blue-600 text-white shadow-md shadow-blue-500/30 ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-800 scale-110 z-10";
                            } else if ( isAvailable )
                            {
                                dayClasses += "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-800/40 cursor-pointer border border-emerald-200 dark:border-emerald-800/50 shadow-sm shadow-emerald-500/10";
                            } else
                            {
                                // Not available in HF
                                dayClasses += "text-red-400 dark:text-red-400/60 bg-red-50/50 dark:bg-red-900/10 hover:bg-slate-100 dark:hover:bg-slate-700/50 cursor-pointer border border-red-100 dark:border-red-900/30";
                            }

                            return (
                                <button
                                    key={ idx }
                                    onClick={ () => isCurrentMonth && handleSelectDate( day ) }
                                    disabled={ !isCurrentMonth }
                                    className={ dayClasses }
                                    title={ isAvailable ? 'Data Available in Cloud' : 'Data Missing / Fallback' }
                                >
                                    { format( day, 'd' ) }
                                    { isCurrentMonth && isAvailable && !isSelected && (
                                        <div className="absolute top-[4px] right-[4px] w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_4px_rgba(16,185,129,0.8)]" />
                                    ) }
                                </button>
                            )
                        } ) }
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700/50 flex items-center justify-between px-1">
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Available</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-red-400" />
                            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Missing</span>
                        </div>
                    </div>

                    { isLoadingDays && (
                        <div className="absolute top-4 left-4 flex gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-ping" />
                        </div>
                    ) }
                </div>,
                document.body
            ) }
        </div>
    );
};
